import { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import { useUser } from '../../../../context/UserContext';
import { getWorkspaceLogo } from '../../../../utils/workspaceUtils';
import { WalletCard, WalletPlatform, WalletVersoBlock } from './creation/tikeo/walletPreview/shared';
import { NotificationScreen, RectoScreen, VersoScreen } from './creation/tikeo/walletPreview/WalletScreens';

export interface WalletPreviewData {
  title: string;
  message: string;
  versoBlocks: WalletVersoBlock[];
  rectoImage?: string;
}

interface WalletPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  data: WalletPreviewData;
}

const SCREENS = [
  { label: 'Notification', Screen: NotificationScreen },
  { label: 'Recto de la carte', Screen: RectoScreen },
  { label: 'Verso de la carte', Screen: VersoScreen },
];

function WalletPreviewDialog({ open, onClose, data }: WalletPreviewDialogProps) {
  const { selectedWorkspace } = useUser();
  const [platform, setPlatform] = useState<WalletPlatform>('android');

  const company = selectedWorkspace?.longName || selectedWorkspace?.shortName || '';
  const card: WalletCard = {
    company,
    logo: selectedWorkspace?.volumeName ? getWorkspaceLogo(selectedWorkspace.volumeName) : '',
    programName: `Le club fidélité ${company}`.trim(),
    rectoImage: data.rectoImage,
    title: data.title.trim(),
    message: data.message.trim(),
    blocks: data.versoBlocks.filter(block => block.content.trim() !== ''),
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Prévisualisation du wallet</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={platform}
            onChange={(_, value: WalletPlatform | null) => value && setPlatform(value)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, gap: 0.75 } }}
          >
            <ToggleButton value="android"><AndroidIcon fontSize="small" />Android</ToggleButton>
            <ToggleButton value="ios"><AppleIcon fontSize="small" />Apple</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 4, pb: 1 }}>
          {SCREENS.map(({ label, Screen }) => (
            <Box key={label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
              <Screen platform={platform} card={card} />
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default WalletPreviewDialog;
